import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import useStyles from "./styles";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { useGetAllEvents } from "../../../../api/event";
import { useServerPagination } from "../../../../hooks/useServerPagination";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../../../components/CustomButtonTable/CustomButtonTable";
import { Sidebar } from "../../../../containers/Sidebar/Sidebar";
import { CustomIconButton } from "../../../../components/CustomIconButton/CustomIconButton";
import { Icon } from "../../../../components/Icon/Icon";
import { CustomTable } from "../../../../components/CustomTable/CustomTable";
import { columnsHistory } from "../data";
import { EventDocumentsModal } from "../EventDocumentsModal/EventDocumentsModal";
import dayjs from "dayjs";

type Props = {};

export const VehicleHistory: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id = "" } = useParams();
  const vehicleId = parseInt(id);
  const fleetId = getCurrentFleetId();

  const pagination = useServerPagination({ initialPerPage: 20 });
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const {
    events,
    totalCount,
    isLoading,
    error,
  } = useGetAllEvents(fleetId, vehicleId, pagination.page, pagination.perPage);

  const handleBack = () => {
    navigate(`/vehicule/update/${vehicleId}`);
  };

  const handleOpenDocumentsModal = (eventId: number) => {
    setSelectedEventId(eventId);
    setDocumentsModalOpen(true);
  };

  const handleCloseDocumentsModal = () => {
    setDocumentsModalOpen(false);
    setSelectedEventId(null);
  };

  const handleViewEventDetail = (eventId: number) => {
    navigate(`/vehicule/update/${vehicleId}/event/${eventId}`);
  };

  // Keyed on `t`: react-i18next returns a new identity on language change, so
  // the headers rebuild instead of staying frozen at their first render.
  const columns = useMemo(
    () => columnsHistory(t, handleOpenDocumentsModal, handleViewEventDetail),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, vehicleId]
  );

  /**
   * Date de la derniere intervention.
   *
   * La deuxieme carte affichait le nombre de PAGES. Ce n'est pas une metrique
   * metier : c'est un detail de pagination, deja dit par "1-1 of 1" en bas du
   * tableau, et il vaut 1 dans l'immense majorite des cas. Une carte de stat
   * doit repondre a une question que l'utilisateur se pose — ici "a quand
   * remonte le dernier passage a l'atelier".
   *
   * Calcule sur la page courante uniquement, et l'API trie par datetime
   * decroissant (Fleet::getEventVehicleById), donc c'est exact en page 1.
   */
  const lastServiceDate = useMemo(() => {
    if (!events || events.length === 0) return null;
    const timestamps = events
      .map((e: any) => e?.datetime)
      .filter(Boolean)
      .map((d: string) => dayjs(d))
      .filter((d: dayjs.Dayjs) => d.isValid());
    if (timestamps.length === 0) return null;
    return timestamps.reduce((latest: dayjs.Dayjs, d: dayjs.Dayjs) =>
      d.isAfter(latest) ? d : latest
    );
  }, [events]);

  if (isNaN(vehicleId)) {
    return (
      <div className={classes.errorContainer}>
        <CustomTypography 
          variant="h2" 
          text={t("error.invalidVehicleId", "Invalid Vehicle ID")} 
        />
        <CustomButtonTable
          variant="default"
          label={t("common.goBack", "Go Back")}
          onClick={() => navigate("/vehicules")}
        />
      </div>
    );
  }

  if (typeof fleetId !== 'number' || isNaN(fleetId) || fleetId < 0) {
    return (
      <div className={classes.errorContainer}>
        <CustomTypography 
          variant="h2" 
          text={t("error.invalidFleetId", "Invalid Fleet ID")} 
        />
        <CustomButtonTable
          variant="default"
          label={t("common.goBack", "Go Back")}
          onClick={() => navigate("/vehicules")}
        />
      </div>
    );
  }

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.historyRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.container}>
        <div className={classes.headerSection}>
          <div className={classes.headerLeft}>
            <CustomIconButton
              iconName="arrowLeft"
              widthIcon="20px"
              heightIcon="20px"
              onClick={handleBack}
            />
            <div className={classes.titleSection}>
              <CustomTypography
                variant="h3"
                text={t(
                  "panel.historyOfInterventions.title",
                  "Service history"
                )}
                lineHeight="40px"
              />
              <div className={classes.vehicleInfo}>
                <Icon name="vehicule" height={24} width={24} />
                <CustomTypography 
                  variant="default" 
                  text={t("vehicule.vehicleId", {
                    defaultValue: "Vehicle ID: {{id}}",
                    id: vehicleId,
                  })}
                />
              </div>
            </div>
          </div>
          <div className={classes.headerButton}>
            <CustomButtonTable
              variant="primary"
              label={t(
                "panel.historyOfInterventions.addIntervention",
                "Add a service entry"
              )}
              iconName="plus"
              onClick={() => navigate(`/vehicule/update/${vehicleId}/create-event`)}
            />
          </div>
        </div>

        <div className={classes.statsSection}>
          <div className={classes.statCard}>
            <CustomTypography
              variant="h3"
              text={(totalCount || 0).toString()}
              color="primary"
            />
            <CustomTypography
              variant="link"
              text={t(
                "panel.historyOfInterventions.totalInterventions",
                "Total service entries"
              )}
            />
          </div>
          <div className={classes.statCard}>
            <CustomTypography
              variant="h3"
              text={
                lastServiceDate
                  ? lastServiceDate.format("DD-MM-YYYY")
                  : t("common.none", "\u2014")
              }
              color="primary"
            />
            <CustomTypography
              variant="link"
              text={t("panel.historyOfInterventions.lastService", "Last service")}
            />
          </div>
        </div>

        {isLoading && (
          <div className={classes.loadingContainer}>
            <CustomTypography
              variant="default"
              text={t("common.loading", "Loading...")}
            />
          </div>
        )}

        {error && (
          <div className={classes.errorContainer}>
            <CustomTypography
              variant="default"
              text={t(
                "error.loadingInterventions",
                "Could not load the service history"
              )}
              color="error"
            />
            <CustomButtonTable
              variant="default"
              label={t("common.retry", "Retry")}
              onClick={() => window.location.reload()}
            />
          </div>
        )}

        {!isLoading && !error && events && events.length > 0 && (
          <div className={classes.tableContainer}>
            <CustomTable
              columns={columns}
              data={events}
              titleHeader={t(
                "panel.historyOfInterventions.allInterventions",
                "All service entries"
              )}
              isFilterFromBackend
              tablePagnationCount={totalCount}
              {...pagination.tableProps}
            />
          </div>
        )}

        {!isLoading && !error && (!events || events.length === 0) && (
          <div className={classes.emptyContainer}>
            {/*
              Une liste vide n'est pas une erreur. Cet etat empruntait
              `errorContainer`, le meme conteneur que l'echec de chargement :
              un vehicule neuf sans intervention avait donc l'air d'un ecran
              casse. `emptyContainer` existait deja dans styles.ts, inutilise.
            */}
            <div className={classes.emptyIcon}>
              <Icon name="file" width={40} height={40} />
            </div>
            <CustomTypography
              variant="h3"
              text={t(
                "panel.historyOfInterventions.noInterventions",
                "No service entries yet"
              )}
            />
            <CustomTypography
              variant="default"
              text={t(
                "panel.historyOfInterventions.noInterventionsHint",
                "Log maintenance, repairs and inspections to build this vehicle's service record."
              )}
            />
            <CustomButtonTable
              variant="primary"
              label={t(
                "panel.historyOfInterventions.createFirst",
                "Add the first service entry"
              )}
              iconName="plus"
              onClick={() => navigate(`/vehicule/update/${vehicleId}/create-event`)}
            />
          </div>
        )}

        {/*
          Le bloc "Actions rapides" est supprime. Il ne contenait qu'un bouton
          "Voir les details du vehicule" pointant sur
          /vehicule/update/{id} — c'est-a-dire exactement la destination de la
          fleche retour en haut de page. Une carte entiere, un titre de
          section et une bordure pour dupliquer un controle deja present a
          l'ecran : cela ajoutait du poids visuel sans ajouter de choix.
        */}
      </div>

      {selectedEventId !== null && (
        <EventDocumentsModal
          open={documentsModalOpen}
          onClose={handleCloseDocumentsModal}
          vehicleId={vehicleId}
          eventId={selectedEventId}
        />
      )}
    </Sidebar>
  );
};